/**
 * @file Hub en ligne : pastille « En ligne · N » (liaison au serveur), pseudos flottants au-dessus des autres joueurs et bulles (emotes / messages)
 * au-dessus de tous les chats, le mien compris (pool DOM, projection 3D -> ecran).
 */
import { Component } from '../core/Component';
import { h, setText } from '../core/dom';
import type { UiContext } from '../UiContext';
import { EMOTE_EMOJI } from './HubSocial';

/** Autres joueurs affiches au plus (celui du rendu : MAX_REMOTE_CATS) + 1 etiquette pour mon propre chat (bulle seulement). */
const MAX_TAGS = 24;
/** Hauteur (u monde) du pseudo au-dessus du chat (le chat mesure ~1.2 x 1.55). */
const TAG_HEIGHT = 2.6;
/** Meme rattrapage que le rendu (render/views/EntityViews) pour que le pseudo suive le chat lisse. */
const FOLLOW = 14;

interface Tag {
  el: HTMLElement;
  nm: HTMLElement;
  bub: HTMLElement;
  x: number;
  y: number;
  id: string | null;
  bubbleText: string;
}

const bubbleLabel = (text: string): string => (text.startsWith('emote:') ? (EMOTE_EMOJI[text.slice(6)] ?? '') : text);

export class HubPresence extends Component {
  private label!: HTMLElement;
  private readonly tags: Tag[] = [];
  private self!: Tag;
  private layer!: HTMLElement;
  private shown = '';

  constructor(ctx: UiContext) {
    super(ctx, 'div', 'hub-presence');
  }

  private makeTag(): Tag {
    const nm = h('span', { class: 'nm' });
    const bub = h('span', { class: 'bub' });
    const el = h('span', { class: 'tag' }, bub, nm);
    this.layer.append(el);
    return { el, nm, bub, x: 0, y: 0, id: null, bubbleText: '' };
  }

  protected build(): void {
    this.label = h('span', {});
    this.el.append(h('span', { class: 'dot' }), this.label);
    // les pseudos vivent dans une couche plein ecran du HUD (et non dans la pastille)
    this.layer = h('div', { class: 'hub-tags' });
    for (let i = 0; i < MAX_TAGS; i++) this.tags.push(this.makeTag());
    this.self = this.makeTag();
    queueMicrotask(() => this.el.closest('.hud')?.append(this.layer));
    this.addDisposer(() => this.layer.remove());
    this.update(0);
  }

  /** Bulle active (emote / message) d'un joueur, ou chaine vide. */
  private bubbleOf(id: string | null): string {
    if (id === null) return '';
    const b = this.ctx.hub?.bubbles.get(id);
    return b && performance.now() < b.until ? bubbleLabel(b.text) : '';
  }

  private place(tag: Tag, x: number, y: number, name: string, bubble: string, visibleName: boolean): void {
    if (tag.nm.textContent !== name) setText(tag.nm, name);
    tag.nm.style.display = visibleName && name ? 'block' : 'none';
    if (tag.bubbleText !== bubble) {
      tag.bubbleText = bubble;
      setText(tag.bub, bubble);
      tag.bub.style.display = bubble ? 'block' : 'none';
    }
    const s = this.ctx.projector.worldToScreen(x, y, TAG_HEIGHT);
    tag.el.style.display = s.visible && (bubble || (visibleName && name)) ? 'flex' : 'none';
    tag.el.style.transform = `translate3d(calc(${s.x.toFixed(1)}px - 50%), calc(${s.y.toFixed(1)}px - 50%), 0)`;
  }

  override update(dt: number): void {
    const hub = this.ctx.hub;
    if (!hub) return;
    const { i18n } = this.ctx;
    const text = hub.link === 'connected' ? `${i18n.t('hub.link.connected')} · ${hub.players.length + 1}` : i18n.t(hub.link === 'connecting' ? 'hub.link.connecting' : 'hub.link.solo');
    if (text !== this.shown) {
      this.shown = text;
      setText(this.label, text);
      this.el.classList.toggle('connected', hub.link === 'connected');
      this.el.classList.toggle('connecting', hub.link === 'connecting');
    }

    const k = 1 - Math.exp(-FOLLOW * Math.max(0, dt));
    const players = hub.link === 'connected' ? hub.players : [];
    // un pseudo par joueur : on reutilise celui deja attribue a l'identifiant, sinon un libre
    const byId = new Map(this.tags.filter((t) => t.id !== null).map((t) => [t.id as string, t]));
    const used = new Set<Tag>();
    for (const p of players.slice(0, MAX_TAGS)) {
      let tag = byId.get(p.id);
      if (!tag) {
        tag = this.tags.find((t) => t.id === null && !used.has(t));
        if (!tag) continue;
        tag.id = p.id;
        tag.x = p.x;
        tag.y = p.y;
      }
      used.add(tag);
      if (Math.hypot(p.x - tag.x, p.y - tag.y) > 12) {
        tag.x = p.x;
        tag.y = p.y;
      } else {
        tag.x += (p.x - tag.x) * k;
        tag.y += (p.y - tag.y) * k;
      }
      this.place(tag, tag.x, tag.y, p.nickname, this.bubbleOf(p.id), true);
    }
    for (const t of this.tags) {
      if (used.has(t)) continue;
      t.id = null;
      t.el.style.display = 'none';
    }

    // mon propre chat : bulle seulement (pas de pseudo)
    const view = this.ctx.sim.getView();
    const lid = view.catIds[0];
    const leader = lid === undefined ? undefined : view.entity(lid);
    const mine = hub.link === 'connected' && leader ? this.bubbleOf(hub.selfId) : '';
    if (leader && mine) this.place(this.self, leader.transform.x, leader.transform.y, '', mine, false);
    else this.self.el.style.display = 'none';
  }
}
